/**
 * Step 2 : Configuration
 * Durée, Plafond financier, Limite kilométrique, Transfer Pricing, Commission, Provision
 */

import { useMemo, useEffect, useState } from 'react';
import { Settings } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';
import { WarrantyFormState } from '@/features/warranties/hooks/useWarrantyFormWizard';
import type { DurationOption } from '@/features/warranties/types/warranty.types';
import { useAllowedDurations } from '@/features/warranties/hooks/useAllowedDurations';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { calculatePriceHT } from '@/features/warranties/services/pricingCalculator';
import { FormInput } from '@/Components/shared/FormInput';
import { InlineSelectInput, type InlineSelectOption } from '@/Components/shared/InlineSelectInput';
import { AssistanceFormulaPicker } from '@/features/assistance/components/AssistanceFormulaPicker';
interface Step2Props {
  formData: WarrantyFormState;
  updateFormData: (updates: Partial<WarrantyFormState>) => void;
  onCanProceedChange: (canProceed: boolean) => void;
  showErrors?: boolean;
}

export const Step2Configuration = ({ formData, updateFormData, onCanProceedChange, showErrors = false }: Step2Props) => {
  const allowedDurations = useAllowedDurations(formData.societyId);
  const { data: config } = useEffectiveConfiguration(formData.societyId);

  // Options de durée pour InlineSelectInput
  const durationOptions = useMemo<InlineSelectOption<string>[]>(() => {
    return (allowedDurations || []).map((duration) => ({
      label: `${duration} mois`,
      value: duration.toString(),
    }));
  }, [allowedDurations]);

  // State local pour le mode de commission
  const [commissionMode, setCommissionMode] = useState<'fixed' | 'percentage'>('percentage');
  const [commissionInputValue, setCommissionInputValue] = useState<number | undefined>(formData.commission);

  // Calculer le HT automatiquement depuis le TTC
  const priceHT = useMemo(() => {
    return formData.sellingPrice ? calculatePriceHT(Number(formData.sellingPrice)) : 0;
  }, [formData.sellingPrice]);

  // Calculer le montant de commission selon le mode
  const calculatedCommission = useMemo(() => {
    if (commissionInputValue === undefined) return undefined;

    if (commissionMode === 'percentage' && priceHT > 0) {
      return (priceHT * commissionInputValue) / 100;
    }

    return commissionInputValue; // montant fixe
  }, [commissionMode, commissionInputValue, priceHT]);

  // Synchroniser avec formData.commission
  useEffect(() => {
    updateFormData({ commission: calculatedCommission });
  }, [calculatedCommission, updateFormData]);

  // Logique mutuelle exclusive Transfer Pricing
  const hasHeadTransferPrice = useMemo(() => {
    return (formData.headTransferPrice ?? 0) > 0;
  }, [formData.headTransferPrice]);

  const hasMemberTransferPrice = useMemo(() => {
    return (formData.memberTransferPrice ?? 0) > 0;
  }, [formData.memberTransferPrice]);

  // Reset automatique si les deux sont remplis
  useEffect(() => {
    if (hasHeadTransferPrice && hasMemberTransferPrice) {
      updateFormData({ memberTransferPrice: 0 });
    }
  }, [hasHeadTransferPrice, hasMemberTransferPrice, updateFormData]);

  // Extension de garantie constructeur : pas de durée ni km
  const isExtension = formData.warrantyType === 2;

  // Auto-set duration=1 et kmLimit=undefined pour les extensions
  useEffect(() => {
    if (isExtension) {
      updateFormData({ duration: 1 as DurationOption, kmLimit: undefined });
    }
  }, [isExtension, updateFormData]);

  // Conditions d'affichage des champs (dérivation pure)
  const showTransferPricing = useMemo(() => {
    return !!(config && config.isTransferPrice);
  }, [config?.isTransferPrice]);

  const showProvision = useMemo(() => {
    return !!(config?.defineProvision);
  }, [config?.defineProvision]);

  const showCommission = useMemo(() => {
    return !!(config?.commission);
  }, [config?.commission]);

  /**
   * Une durée déjà présente dans le formulaire (venant d'un preset, ou d'une
   * session restaurée avant un changement de société) peut ne pas faire partie
   * des durées autorisées pour la société sélectionnée : le `<select>` n'a
   * alors aucune option correspondante et n'affiche rien, alors que la valeur
   * est bien là. Sans ce contrôle, la garantie partait avec une durée que la
   * société n'a pas le droit de vendre, silencieusement.
   */
  const isDurationAllowed = useMemo(() => {
    if (isExtension || formData.duration === undefined) return true;
    return allowedDurations.includes(formData.duration);
  }, [isExtension, formData.duration, allowedDurations]);

  // Validation dérivée
  const canProceed = useMemo(() => {
    if (!isDurationAllowed) return false;

    // Extension : seul le plafond financier est requis
    if (isExtension) {
      if (!formData.financialCap || formData.financialCap <= 0) return false;
    } else {
      // Champs de base
      if (
        formData.duration === undefined ||
        !formData.financialCap ||
        formData.financialCap <= 0 ||
        !formData.kmLimit ||
        formData.kmLimit <= 0
      ) {
        return false;
      }
    }

    // Transfer pricing : obligatoire si affiché (au moins un des deux)
    if (showTransferPricing) {
      const hasHead = (formData.headTransferPrice ?? 0) > 0;
      const hasMember = (formData.memberTransferPrice ?? 0) > 0;
      if (!hasHead && !hasMember) return false;
    }

    // Provision : obligatoire si affichée
    if (showProvision) {
      if (formData.provision === undefined || formData.provision === null) return false;
    }

    // Commission : obligatoire si affichée (0 est une valeur valide)
    if (showCommission) {
      if (formData.commission === undefined || formData.commission === null) return false;
    }

    return true;
  }, [
    isExtension,
    isDurationAllowed,
    formData.duration,
    formData.financialCap,
    formData.kmLimit,
    formData.headTransferPrice,
    formData.memberTransferPrice,
    formData.provision,
    formData.commission,
    showTransferPricing,
    showProvision,
    showCommission,
  ]);

  // Remonter la validité au container
  useEffect(() => {
    onCanProceedChange(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4">

      <div className="tw-relative tw-p-6 tw-space-y-5">

        {/* Titre de la step */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
          <Settings className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Configuration</h4>
        </div>

        {/* Durée — masquée pour les extensions */}
        {isExtension ? (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-affinicar-primary">
            <p className="tw-text-sm tw-text-affinicar-primary tw-mb-0 tw-font-medium">
              Extension de garantie constructeur : la durée de la garantie sera définie au moment de la création du contrat.
            </p>
          </div>
        ) : (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Choisissez la durée de couverture
              <Tooltip content="Durée de couverture du contrat en mois" />
            </p>
            <div id="duration-select">
              <InlineSelectInput<string>
                options={durationOptions}
                value={formData.duration?.toString() || null}
                onChange={(value) => updateFormData({ duration: parseInt(value) as DurationOption })}
                name="duration"
                required
                align="left"
                size="md"
              />
              {showErrors && formData.duration === undefined && (
                <p className="tw-mt-1 tw-text-sm tw-text-red-600">La durée est obligatoire</p>
              )}
              {!isDurationAllowed && (
                <p className="tw-mt-1 tw-text-sm tw-text-red-600">
                  La durée de {formData.duration} mois n'est pas proposée par cette société. Choisissez une durée ci-dessus.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Offre d'assistance — même composant, et désormais même carte, qu'en
            modification de garantie. Rien à habiller ici : le bloc porte sa propre
            présentation, pour qu'aucun point d'appel ne puisse la faire diverger. */}
        <AssistanceFormulaPicker
          societyId={formData.societyId}
          duration={formData.duration}
          assistanceEnabled={config?.assistanceEnabled}
          value={formData.assistanceFormulaId ?? null}
          onChange={(assistanceFormulaId) => updateFormData({ assistanceFormulaId })}
        />

        {/* Plafond financier */}
        <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
            Définissez le montant maximum de couverture
            <Tooltip content="Montant maximum pour la prise en charge des réparations" />
          </p>
          <div id="financial-cap-input">
            <FormInput
              type="number"
              value={String(formData.financialCap || '')}
              onChange={(value) => updateFormData({ financialCap: parseInt(value) || undefined })}
              label="Plafond financier (€)"
              placeholder="Ex: 5000"
              suffix="€"
              required
              error={showErrors && (!formData.financialCap || formData.financialCap <= 0) ? 'Le plafond financier est obligatoire' : undefined}
            />
          </div>
        </div>

        {/* Limite kilométrique — masquée pour les extensions */}
        {!isExtension && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Définissez le kilométrage maximum couvert
              <Tooltip content="Kilométrage au-delà duquel la couverture ne s'applique plus" />
            </p>
            <div id="km-limit-input">
              <FormInput
                type="number"
                value={String(formData.kmLimit || '')}
                onChange={(value) => updateFormData({ kmLimit: parseInt(value) || undefined })}
                label="Limite kilométrique (km)"
                placeholder="Ex: 150000"
                suffix="km"
                required
                error={showErrors && (!formData.kmLimit || formData.kmLimit <= 0) ? 'La limite kilométrique est obligatoire' : undefined}
              />
            </div>
          </div>
        )}

        {isExtension && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-affinicar-primary">
            <p className="tw-text-sm tw-text-affinicar-primary tw-mb-0 tw-font-medium">
              Pas de limite kilométrique pour les extensions de garantie constructeur.
            </p>
          </div>
        )}

        {/* Paramètres financiers (si applicable) */}
        {(showProvision || showTransferPricing || showCommission) && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Configurez les paramètres financiers avancés
              <Tooltip content="Provision, rétrocession, achat et commission liés au contrat" />
            </p>

            {/* Provision */}
            {showProvision && (
              <div id="provision-input">
                <FormInput
                  type="number"
                  value={String(formData.provision ?? '')}
                  onChange={(value) => updateFormData({ provision: value === '' ? undefined : parseFloat(value) })}
                  label="Provision (€ HT)"
                  placeholder="0.00"
                  suffix="€"
                  required
                  tooltip="Montant mis de côté pour les SAV potentiels"
                  error={showErrors && showProvision && (formData.provision === undefined || formData.provision === null) ? 'La provision est obligatoire' : undefined}
                />
              </div>
            )}

            {/* Transfer Pricing */}
            {showTransferPricing && (
              <div id="transfer-pricing-group">
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  <div>
                    <FormInput
                      type="number"
                      value={String(formData.headTransferPrice || '')}
                      onChange={(value) => updateFormData({ headTransferPrice: parseFloat(value) || undefined })}
                      label="Prix de rétrocession (€ HT)"
                      placeholder="0.00"
                      suffix="€"
                      required
                      disabled={hasMemberTransferPrice}
                    />
                    {hasMemberTransferPrice && (
                      <p className="tw-text-xs tw-text-amber-600 tw-mt-1">
                        Désactivé car Prix d'achat est rempli
                      </p>
                    )}
                  </div>
                  <div>
                    <FormInput
                      type="number"
                      value={String(formData.memberTransferPrice || '')}
                      onChange={(value) => updateFormData({ memberTransferPrice: parseFloat(value) || undefined })}
                      label="Prix d'achat (€ HT)"
                      placeholder="0.00"
                      suffix="€"
                      required
                      disabled={hasHeadTransferPrice}
                    />
                    {hasHeadTransferPrice && (
                      <p className="tw-text-xs tw-text-amber-600 tw-mt-1 tw-mb-0">
                        Désactivé car Prix de rétrocession est rempli
                      </p>
                    )}
                  </div>
                </div>
                {showErrors && !hasHeadTransferPrice && !hasMemberTransferPrice && (
                  <p className="tw-mt-1 tw-text-sm tw-text-red-600">Au moins un prix de transfert est obligatoire</p>
                )}
              </div>
            )}

            {/* Commission vendeur */}
            {showCommission && (
              <div id="commission-input">
                <div className="tw-relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={commissionMode === 'percentage' ? 100 : undefined}
                    value={commissionInputValue ?? ''}
                    onChange={(e) => setCommissionInputValue(e.target.value === '' ? undefined : parseFloat(e.target.value))}
                    placeholder=" "
                    className={`tw-w-full tw-pl-3 tw-pr-[160px] tw-pt-5 tw-pb-1.5 tw-text-base tw-border tw-rounded-xl tw-transition-all tw-peer tw-placeholder-transparent
                      tw-border-gray-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary`}
                  />
                  <label
                    className={`tw-absolute tw-left-3 tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
                      ${commissionInputValue !== undefined && commissionInputValue !== null
                        ? 'tw-top-1.5 tw-text-[11px] tw-text-affinicar-primary'
                        : 'tw-top-[13px] tw-text-base tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px] peer-focus:tw-text-affinicar-primary'
                      }`}
                  >
                    Commission vendeur ({commissionMode === 'percentage' ? '% du prix total TTC' : '€ TTC'})
                  </label>

                  {/* Toggle % / € intégré à droite de l'input */}
                  <div className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center tw-bg-gray-100 tw-rounded-lg tw-p-0.5">
                    <button
                      type="button"
                      onClick={() => setCommissionMode('percentage')}
                      className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all ${
                        commissionMode === 'percentage'
                          ? 'tw-bg-white tw-text-affinicar-primary tw-shadow-sm'
                          : 'tw-text-gray-500 hover:tw-text-gray-700'
                      }`}
                    >
                      %
                    </button>
                    <button
                      type="button"
                      onClick={() => setCommissionMode('fixed')}
                      className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-md tw-transition-all ${
                        commissionMode === 'fixed'
                          ? 'tw-bg-white tw-text-affinicar-primary tw-shadow-sm'
                          : 'tw-text-gray-500 hover:tw-text-gray-700'
                      }`}
                    >
                      €
                    </button>
                  </div>
                </div>
                {commissionMode === 'percentage' && calculatedCommission !== undefined && priceHT > 0 && (
                  <p className="tw-text-xs tw-text-affinicar-primary tw-mt-1 tw-mb-0">
                    Montant calculé : {calculatedCommission.toFixed(2)} €
                  </p>
                )}
                {showErrors && showCommission && (formData.commission === undefined || formData.commission === null) && (
                  <p className="tw-mt-1 tw-text-sm tw-text-red-600">La commission est obligatoire</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
